import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Box } from '@mui/system'
import { CircularProgress, Collapse, IconButton, useTheme } from '@mui/material'
import { ChevronRight } from '@mui/icons-material'
import { capitalize } from '../../../../utils/functions'
import CustomLinearProgress from '../../../../components/muiComponents/customLinearProgress'
import {
    TestAnalysisState,
    VideoAnalysisState,
} from '../../../../features/types'
import { testApiSlice } from '../../../../features/section/testSlice'
import { videoLessonApiSlice } from '../../../../features/section/videoLessonSlice'
import { TEST, VIDEO } from '../../../../utils/constants'
import Section from './Section'
import RoundedCheck from '../../../../assets/icons/RoundedCheck.svg'

interface Props {
    topic: VideoAnalysisState | TestAnalysisState
    studentId: number
    type: string
}

const TopicProgressCard: React.FC<Props> = ({ topic, type, studentId }) => {
    const theme = useTheme()
    const [expanded, setExpanded] = useState(false)
    const { formatMessage, locale } = useIntl()
    const { data: tests = [], isFetching: isFetchingTests } =
        testApiSlice.useGetStudentTestAnalysisQuery(
            {
                courseTopicId: topic.courseTopicId,
                studentId,
            },
            { skip: !expanded || type === VIDEO },
        )

    const { data: videoLessons = [], isFetching: isFetchingVideoLessons } =
        videoLessonApiSlice.useGetStudentVideoLessonAnalysisQuery(
            {
                courseTopicId: topic.courseTopicId,
                studentId,
            },
            { skip: !expanded || type === TEST },
        )

    const sections = type === VIDEO ? videoLessons : tests
    const styles = {
        wrapper(open: boolean): React.CSSProperties {
            return {
                border: open ? `1px solid #FFB7DF` : `1px solid ${theme.palette.grey[100]}`,
                borderRadius: '8px',
                marginBottom: '16px',
            }
        },
        container: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            p: 1,
            pr: 2,
        },
        chevron: {
            transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
            transition: '0.15s ease-in-out',
        },
        flexCenter: {
            display: 'flex',
            alignItems: 'center',
        },
        flexBetween: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        topicProgress: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            marginLeft: 1,
        },
        linearProgress: {
            backgroundcolor: '#E7EDF7',
            barbackgroundcolor: '#4F72EA',
        },
        countVideo: {
            fontSize: '14px',
            padding: '4px 8px',
            backgroundColor: '#E7EDF7',
            color: '#1B998B',
            borderRadius: '100px',
            marginLeft: '12px',
        },
    }

    const getSectionCount = () => {
        if (type === VIDEO) {
            return (
                (topic as VideoAnalysisState)?.countVideo +
                ' ' +
                formatMessage({ id: 'video' })
            )
        }

        return `${(topic as TestAnalysisState)?.countTest || 0} ${formatMessage({ id: 'test' })}`;
    }

    return (
        <Box sx={styles.wrapper(expanded)}>
            <Box
                sx={styles.container}
                onClick={() => {
                    setExpanded(!expanded)
                }}
            >
                <Box sx={{ ...styles.flexCenter, width: '95%' }}>
                    <IconButton>
                        <ChevronRight sx={styles.chevron} />
                    </IconButton>

                    <Box sx={styles.topicProgress}>
                        <Box
                            mb={1}
                            sx={styles.flexBetween}
                        >
                            <Box sx={styles.flexCenter}>
                                {topic[`topicTitle${capitalize(locale)}`]}
                                <Box style={styles.countVideo}>{getSectionCount()}</Box>
                            </Box>

                            <span style={{ fontWeight: 600 }}>{topic.percentage}%</span>
                        </Box>

                        <Box>
                            <CustomLinearProgress
                                variant="determinate"
                                value={topic.percentage}
                                {...styles.linearProgress}
                            />
                        </Box>


                    </Box>
                </Box>
                {topic.percentage === 100 && <img src={RoundedCheck} alt="RoundedCheck" />}
            </Box>

            <Collapse in={expanded}>
                {isFetchingTests || isFetchingVideoLessons ? (
                    <CircularProgress sx={{ m: 1 }} />
                ) : (
                    <Box>
                        {sections.map((section, index) => (
                            <Section
                                key={index}
                                section={section}
                                studentId={studentId}
                                isLast={index === sections.length - 1}
                                type={type}
                            />
                        ))}
                    </Box>
                )}
            </Collapse>
        </Box>
    )
}

export default TopicProgressCard
